// Seis KPIs de Outbound Loads.
//
// «Reserva incompleta» sustituye al «Stock shortage» del mockup: ese mide
// rotura de stock —que aquí no se puede calcular, `qtyReservada` no es
// disponibilidad— y lo que sí es medible es el hueco de reserva contra lo
// solicitado, que es un aviso distinto y con su propio dato.
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { KpiCard } from '@/components/planning/KpiCard'
import type { CargaSalida } from '@/types'
import type { GrupoFecha } from '@/utils/agruparPorFecha'
import { estadoPickingDe, reservaDe, totalesCargas } from '@/utils/cargasSalida'
import { fmtCajas, fmtQty } from './formatoSalida'

interface Props {
  dark: boolean
  cargas: CargaSalida[]
  grupos: GrupoFecha<CargaSalida>[]
  isLoading: boolean
}

export function CargasSalidaKpis({ dark, cargas, grupos, isLoading }: Props) {
  const { t } = useTranslation()

  const kpis = useMemo(() => {
    let preparadas = 0, enPreparacion = 0, sinEmpezar = 0, reservaIncompleta = 0
    for (const c of cargas) {
      const estado = estadoPickingDe(c)
      if (estado === 'preparada') preparadas++
      else if (estado === 'preparando') enPreparacion++
      else sinEmpezar++
      if (!reservaDe(c).completa) reservaIncompleta++
    }
    const atrasadas = grupos.find(g => g.bucket === 'atrasadas')?.items.length ?? 0
    return { preparadas, enPreparacion, sinEmpezar, reservaIncompleta, atrasadas }
  }, [cargas, grupos])

  const totales = useMemo(() => totalesCargas(cargas), [cargas])

  const v = (n: number) => (isLoading ? '—' : String(n))

  // «palets sin dato» y no «0 palets»: el campo nace null en el 100% de las
  // cargas, y un 0 diría que ninguna lleva palets.
  const subVivas = isLoading
    ? ''
    : t('cargasSalida.kpi.vivasSub', {
        unidades: fmtQty(totales.unidades),
        cajas: totales.cajas != null ? fmtCajas(totales.cajas) : t('cargasSalida.kpi.sinCajas'),
      })

  return (
    <div className="flex gap-3 flex-wrap">
      <KpiCard dark={dark}
        label={t('cargasSalida.kpi.vivas')}
        value={v(cargas.length)}
        sub={subVivas} />
      <KpiCard dark={dark}
        label={t('cargasSalida.kpi.preparadas')}
        value={v(kpis.preparadas)}
        sub={t('cargasSalida.kpi.preparadasSub')}
        valueAccent="emerald" />
      <KpiCard dark={dark}
        label={t('cargasSalida.kpi.enPreparacion')}
        value={v(kpis.enPreparacion)}
        sub={t('cargasSalida.kpi.enPreparacionSub')}
        valueAccent={kpis.enPreparacion > 0 ? 'amber' : undefined} />
      <KpiCard dark={dark}
        label={t('cargasSalida.kpi.sinEmpezar')}
        value={v(kpis.sinEmpezar)}
        sub={t('cargasSalida.kpi.sinEmpezarSub')} />
      <KpiCard dark={dark}
        label={t('cargasSalida.kpi.reservaIncompleta')}
        value={v(kpis.reservaIncompleta)}
        sub={t('cargasSalida.kpi.reservaIncompletaSub')}
        valueAccent={kpis.reservaIncompleta > 0 ? 'amber' : undefined} />
      <KpiCard dark={dark}
        label={t('cargasSalida.kpi.atrasadas')}
        value={v(kpis.atrasadas)}
        sub={t('cargasSalida.kpi.atrasadasSub')}
        valueAccent={kpis.atrasadas > 0 ? 'red' : undefined} />
    </div>
  )
}
